/**
 * @mhmo91/ican/firestore — adapter types.
 *
 * The adapter is invoked from two seams:
 *
 * 1. **Entity authoring** — `entity().pipe(...).adaptor(firestore({...}))`
 *    attaches a `FirestoreAdapterBinding` to the entity. The kernel
 *    preserves it on the entity's `_adapters` array; the adapter
 *    filters by the `_adapter: 'firestore'` discriminator at emit time.
 *
 * 2. **Deploy** — `firestore.collectBindings(policy)` walks the
 *    policy's entities, reads each one's firestore binding, groups
 *    entities by `database`, and returns one `Binding` per distinct
 *    database alias. The CLI / equivalence harness consume those
 *    bindings.
 */
import type { AnyEntity, Policy } from '@mhmo91/ican';

export const FIRESTORE_ADAPTER_TAG = 'firestore' as const;

/**
 * What `firestore({...})` returns. Attached to an entity via
 * `.adaptor(firestore({...}))`.
 *
 * `path` uses Firestore-rules placeholder syntax — segments wrapped
 * in `{}` are wildcards.
 *
 * `database` is the alias the entity's rules deploy to. Empty string
 * (the default) maps to the (default) Firestore database; a non-empty
 * alias maps to a named database.
 */
export type FirestoreAdapterBinding = {
  readonly _adapter: typeof FIRESTORE_ADAPTER_TAG;
  readonly path: string;
  readonly hierarchicalGrants?: boolean;
  /** Canonical lookups use bound {pathVars} or explicit declared {data.foreignKey} fields. */
  readonly parents?: Readonly<Record<string, string>>;
  readonly database?: string;
};

/**
 * Output of `firestore.collectBindings(policy)`. One `Binding` per
 * distinct database alias detected on the policy's entities.
 *
 * `extraMatches` is an optional list of verbatim Firestore-rules `match`
 * blocks (the full `match /path/... { ... }` text, indented with four
 * spaces) that the emitter appends inside the
 * `match /databases/{database}/documents` scope, before the default-deny
 * tail. Use this for non-entity collections that need a hand-authored rule
 * but must survive regeneration — e.g. a DEV-only signal channel.
 */
export type Binding = {
  readonly database: string;
  readonly entries: ReadonlyArray<BoundEntity>;
  readonly extraMatches?: ReadonlyArray<string>;
};

export type BoundEntity = {
  readonly name: string;
  readonly entity: AnyEntity;
  readonly path: string;
  readonly hierarchicalGrants?: boolean;
  /** Canonical lookups use bound {pathVars} or explicit declared {data.foreignKey} fields. */
  readonly parents?: Readonly<Record<string, string>>;
};

/**
 * Type-level constraint helper: ensures the policy is comprised
 * exclusively of entities that carry a Firestore adapter binding.
 */
export type FirestoreReady<P extends Policy> = P;
